import { RightOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { useMemoizedFn } from 'ahooks';
import { Form, FormProps, Input, Space } from 'antd';
import {
  parse as phoneParser,
  format as phoneFormatter,
  isSupportedCountry,
  isValidNumber,
  isValidNumberForRegion,
} from 'libphonenumber-js';
import { isUndefined } from 'lodash-es';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import CheckboxGroup, { CheckboxGroupOption } from './CheckboxGroup';
import { ShopTypeEnum } from './ShopeeCNSCGuide';
import { BottomButton, BottomFooter } from './styles';
import { useOrgConfigStateValue } from 'hooks/business/useOrgConfig';
import { isInCN } from 'utils/is';

const FormContainer = styled(Form)`
  min-height: 280px;

  .ant-form-item-label {
    font-weight: 500;
  }
`;
const AccountInput = styled(Input)`
  width: 260px;
`;

export enum ShopeeAuthWay {
  Official,
  External,
  Account,
}

export interface ShopeeAuthShopParams {
  region: General.UpperCaseRegion | 'CNSC';
  storeType: IM_API.Admin.StoreType;
  shopType?: ShopTypeEnum;
  account?: string;
  password?: string;
  departmentIds?: number[];
}

export interface ShopeeShopAuthWayProps {
  form: FormProps['form'];
  chatPartnerType?: boolean;
  erpPartnerType?: boolean;
  topContent?: React.ReactNode;
  authWay?: ShopeeAuthWay;
  onChange?: (value: ShopeeAuthWay) => void;
  onNext?: (value: ShopeeAuthShopParams) => void;
}

const ShopeeShopAuthWay = (props: ShopeeShopAuthWayProps) => {
  const { form, topContent, authWay, onChange, onNext } = props;
  const { t } = useTranslate();
  const [authWayOptions, setAuthWayOptions] = useState<CheckboxGroupOption[]>([]);
  const [isReady, setIsReady] = useState(!isUndefined(authWay));
  const { whiteListConfig: { SHOPEE_BATCH_AUTH = false } = {} } = useOrgConfigStateValue();

  const valuesChange = useMemoizedFn((values: any) => {
    if (values.authWay === ShopeeAuthWay.Account) {
      setIsReady(!!values.account && !!values.password);
    } else {
      setIsReady(true);
    }
  });

  /**
   * 初始化授权方式选项
   * PD: https://leyan.yuque.com/czpqnb/pd/ohezvbqx9qkd9c33#ikLAI
   */
  const initAuthWayOptions = useMemoizedFn(() => {
    let options: CheckboxGroupOption[] = [];
    const shopType = form?.getFieldValue('shopType');
    // 本土店铺
    if (shopType === ShopTypeEnum.Local) {
      // 是否是大陆境内
      const isCN = isInCN();
      if (isCN && SHOPEE_BATCH_AUTH) {
        options = [
          {
            render: (
              <>
                {t('authShop.authWayOption1')}
                <span className="bg-[var(--fett-surface-warning-bg)] text-[var(--fett-surface-warning-text)] text-xs px-1 py-2 ml-1">
                  {t('authShop.本土店铺推荐使用方法3')}
                </span>
              </>
            ),
            value: ShopeeAuthWay.Account,
          },
          {
            render: t('authShop.authWayOption2'),
            value: ShopeeAuthWay.Official,
          },
          {
            render: t('authShop.authWayOption5'),
            value: ShopeeAuthWay.External,
          },
        ];
      } else {
        options = [
          {
            render: t('authShop.authWayOption3'),
            value: ShopeeAuthWay.Official,
          },
          {
            render: t('authShop.authWayOption5'),
            value: ShopeeAuthWay.External,
          },
        ];
      }
    }

    // 跨境店铺 (官方仓)
    if (shopType === ShopTypeEnum.CrossOfficial || shopType === ShopTypeEnum.Cross3PF) {
      options = [
        {
          render: t('authShop.authWayOption4'),
          value: ShopeeAuthWay.Official,
        },
        {
          render: t('authShop.authWayOption6'),
          value: ShopeeAuthWay.External,
        },
      ];
    }

    setAuthWayOptions(options);

    const authWay = options?.[0]?.value as ShopeeAuthWay;
    form?.setFieldsValue({ authWay });
    onChange?.(authWay);
    valuesChange({ authWay });
  });

  const accountVisible = useMemo(() => {
    const shopType = form?.getFieldValue('shopType');
    return shopType === ShopTypeEnum.Local && authWay === ShopeeAuthWay.Account;
  }, [authWay, form]);

  const formatAccount = useCallback(
    (value: string, region: string) => {
      const upperRegion = region.toUpperCase() as General.UpperCaseRegion;
      if (
        value &&
        region !== 'CNSC' &&
        isSupportedCountry(upperRegion) &&
        (isValidNumber(value) || isValidNumberForRegion(value, upperRegion))
      ) {
        const phoneNumber = phoneParser(value, upperRegion);
        form?.setFieldsValue({ account: phoneFormatter(phoneNumber, 'E.164') });
      }
    },
    [form],
  );

  useEffect(() => {
    initAuthWayOptions();
  }, [initAuthWayOptions]);

  return (
    <FormContainer
      form={form}
      layout="vertical"
      onValuesChange={(_, values: any) => {
        valuesChange(values);
      }}
    >
      {topContent}
      <Form.Item label={t('authShop.您可选择以下任意方式添加店铺')} name="authWay" required>
        <CheckboxGroup
          layout="vertical"
          options={authWayOptions}
          onChange={(val) => onChange && onChange(val as ShopeeAuthWay)}
        />
      </Form.Item>

      {accountVisible ? (
        <Form.Item label={t('authShop.Shopee帐号信息')} required>
          <Space size={24}>
            <Form.Item name="account" required>
              <AccountInput
                placeholder={t('authShop.请输入帐号')}
                onBlur={(e) => {
                  const { value } = e.target;
                  const region = form?.getFieldValue('region');
                  value && region && formatAccount(value, region);
                }}
              />
            </Form.Item>
            <Form.Item name="password" required>
              <AccountInput placeholder={t('authShop.请输入密码')} />
            </Form.Item>
          </Space>
        </Form.Item>
      ) : null}

      <BottomFooter>
        <BottomButton
          disabled={!isReady}
          size="large"
          type="primary"
          onClick={() => {
            const storeType = form?.getFieldValue('storeType');
            const region = form?.getFieldValue('region');
            const shopType = form?.getFieldValue('shopType');

            if (onNext && storeType && region) {
              onNext &&
                onNext({
                  shopType,
                  storeType,
                  region,
                  account: form?.getFieldValue('account'),
                  password: form?.getFieldValue('password'),
                  departmentIds: form?.getFieldValue('departmentIds'),
                });
            }
          }}
        >
          {t('authShop.下一步')}
          <RightOutlined />
        </BottomButton>
      </BottomFooter>
    </FormContainer>
  );
};

export default ShopeeShopAuthWay;
